import React, { createContext, useContext, useState, useRef, useCallback, useEffect, memo } from "react";
import PropTypes from "prop-types";
import { proxiedUrlFor } from "../../utils/media-url-utils";
import aiAgents from "../../singleton/AIAgents";
const VADContext = createContext();
import { MediaDevicesEvents } from "../../utils/media-devices-utils";

export const useVAD = () => useContext(VADContext);

export const VADProvider = memo(function VADProvider({
  children,
  entered,
  onSpeechEndCallback,
  onSpeechStartCallback,
  options = {},
  micActiveAI
}) {
  const [vadInstance, setVadInstance] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [scriptsLoaded, setScriptsLoaded] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [isWindowFocused, setIsWindowFocused] = useState(document.hasFocus());
  const [agentsCount] = useState(aiAgents.agents.length);
  const [chatRecording, setChatRecording] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false);
  const [isTalking, setIsTalking] = useState(false);

  const vadRef = useRef();
  const activeTrackIdRef = useRef(null);
  const mediaDevicesManager = useRef(APP.mediaDevicesManager).current;

  const configureOrt = useCallback(async () => {
    // guard so HMR or re-mounts don't run this twice
    if (!window.ort || window.__ortConfigured) {
      return;
    }
    window.__ortConfigured = true;

    window.ort.env.wasm.wasmPaths = proxiedUrlFor("https://cdnjs.cloudflare.com/ajax/libs/onnxruntime-web/1.17.3/");
    window.ort.env.wasm.simd = false;
    window.ort.env.wasm.numThreads = 1;
    window.ort.env.wasm.debug = false;
  }, []);

  const cleanupVAD = useCallback(() => {
    if (vadRef.current) {
      try {
        vadRef.current.pause(); // Stop processing
        if (vadRef.current.destroy) {
          vadRef.current.destroy(); // Clean up resources
        }
      } catch (error) {
        console.error("Error pausing VAD instance during cleanup:", error);
      }
      vadRef.current = null;
      setVadInstance(null);
      setIsRecording(false);
      activeTrackIdRef.current = null; // Clear the active track ID
    }
  }, []);

  useEffect(() => {
    return () => {
      if (vadRef.current) {
        vadRef.current.pause(); // stop worklet callback loop
        setVadInstance(null);
        setIsRecording(false);
      }
    };
  }, []);

  useEffect(() => {
    try {
      vadRef.current = vadInstance;
    } catch (error) {
      console.error("Error updating vadRef:", error);
    }
  }, [vadInstance]);

  const startRecording = useCallback(() => {
    try {
      if (vadRef.current && micActiveAI && !isTalking) {
        vadRef.current.start();
        setIsRecording(true);
      }
    } catch (error) {
      console.error("AIChatContext: Error starting recording:", error);
      setIsRecording(false);
    }
  }, [micActiveAI, isTalking]);

  const stopRecording = useCallback(() => {
    try {
      if (vadRef.current) {
        vadRef.current.pause();
        setIsRecording(false);
      }
    } catch (error) {
      console.error("AIChatContext: Error stopping recording:", error);
      setIsRecording(false);
    }
  }, []);

  useEffect(() => {
    if (vadRef.current && !micActiveAI) {
      stopRecording();
      vadRef.current.pause();
    }
  }, [micActiveAI, stopRecording]);

  const setup = useCallback(async () => {
    try {
      await configureOrt();

      const audioTrack = mediaDevicesManager.audioTrack;

      if (!audioTrack || !audioTrack.readyState === "ended") {
        if (vadRef.current) cleanupVAD();
        return;
      }

      if (vadRef.current && activeTrackIdRef.current === audioTrack.id) {
        return;
      }

      if (vadRef.current && activeTrackIdRef.current !== audioTrack.id) {
        cleanupVAD(); // Pause old instance and clear refs
      }

      const sharedStream = new MediaStream([audioTrack]);

      window.myVadSpeaker = await window.vad.MicVAD.new({
        stream: sharedStream,
        onSpeechStart: () => {
          try {
            window.dispatchEvent(new CustomEvent("ai-recording-start"));
            if (onSpeechStartCallback) onSpeechStartCallback();
          } catch (error) {
            console.error("Error in onSpeechStart callback:", error);
          }
        },
        onSpeechEnd: audio => {
          try {
            const wavBuffer = window.vad.utils.encodeWAV(audio);

            const aiAudioListener = document.querySelector("#ai-audio-listener");
            if (aiAudioListener) {
              window.dispatchEvent(new CustomEvent("ai-recording-end", { detail: wavBuffer }));
            }
            if (onSpeechEndCallback) onSpeechEndCallback(wavBuffer);

          } catch (error) {
            console.error("Error in onSpeechEnd callback:", error);
          }
        },
        ...options
      });

      vadRef.current = window.myVadSpeaker;
      setVadInstance(window.myVadSpeaker);
      activeTrackIdRef.current = audioTrack.id; // Store the ID of the track being used
    } catch (e) {
      console.error("Error setting up VAD:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configureOrt, options, cleanupVAD, onSpeechStartCallback, onSpeechEndCallback]);

  useEffect(() => {
    try {
      if (scriptsLoaded && entered && !initialized && agentsCount > 0) {
        setInitialized(true);
        setup();
      }
    } catch (error) {
      console.error("Error in initialization effect:", error);
    }
  }, [scriptsLoaded, entered, setup, initialized, agentsCount]);

  useEffect(() => {
    const loadScript = src => {
      return new Promise((resolve, reject) => {
        try {
          const script = document.createElement("script");
          script.src = src;
          script.async = true;
          script.onload = () => resolve(src);
          script.onerror = () => reject(new Error(`Script load error for ${src}`));
          document.head.appendChild(script);
        } catch (error) {
          reject(error);
        }
      });
    };

    const scripts = [
      proxiedUrlFor(`https://cdn.jsdelivr.net/npm/onnxruntime-web@1.17.3/dist/ort.min.js`),
      proxiedUrlFor(`https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@0.0.18/dist/bundle.min.js`)
    ];

    const loadScriptsSequentially = async scripts => {
      try {
        for (const script of scripts) {
          await loadScript(script);

          if (script.includes("onnxruntime-web")) {
            await configureOrt();
          }
        }
        setScriptsLoaded(true);
      } catch (error) {
        console.error("Error loading scripts sequentially:", error);
        setScriptsLoaded(false);
      }
    };

    loadScriptsSequentially(scripts);

    return () => {
      try {
        vadRef?.current?.pause();
        if (vadInstance) {
          setIsRecording(false);
        }

        if (window.myVadSpeaker) {
          window.myVadSpeaker = null;
          setVadInstance(null);
        }
      } catch (error) {
        console.error("Error in cleanup:", error);
      }
    };
  }, []);

  useEffect(() => {
    // Don't run if scripts aren't loaded or not entered
    if (!scriptsLoaded || agentsCount === 0) {
      // If conditions become unmet, ensure cleanup
      if (vadRef.current) cleanupVAD();
      return;
    }

    const eventTarget = mediaDevicesManager._scene || window.APP; // Use scene or global app event emitter

    const handleMicStarted = () => {
      setTimeout(setup, 50);
    };

    const handleMicEnded = () => {
      cleanupVAD();
    };

    eventTarget.addEventListener(MediaDevicesEvents.MIC_SHARE_STARTED, handleMicStarted);
    eventTarget.addEventListener(MediaDevicesEvents.MIC_SHARE_ENDED, handleMicEnded);

    setup();

    return () => {
      eventTarget.removeEventListener(MediaDevicesEvents.MIC_SHARE_STARTED, handleMicStarted);
      eventTarget.removeEventListener(MediaDevicesEvents.MIC_SHARE_ENDED, handleMicEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptsLoaded, agentsCount]); // Dependencies trigger re-evaluation

  useEffect(() => {
    try {
      const handleFocus = () => {
        setIsWindowFocused(true);
      };

      const handleBlur = () => {
        setIsWindowFocused(false);
      };

      window.addEventListener("focus", handleFocus);
      window.addEventListener("blur", handleBlur);

      setIsWindowFocused(document.hasFocus());

      return () => {
        window.removeEventListener("focus", handleFocus);
        window.removeEventListener("blur", handleBlur);
      };
    } catch (error) {
      console.error("Error in focus/blur effect:", error);
    }
  }, []);

  useEffect(() => {
    if (isTalking) {
      stopRecording();
    }
  }, [isTalking]);

  useEffect(() => {
    try {
      if (vadInstance && scriptsLoaded && entered && micActiveAI && (voiceMode || chatRecording) && !isTalking) {
        if (isWindowFocused) {
          startRecording();
        } else {
          stopRecording();
        }
      }
    } catch (error) {
      console.error("Error in recording effect:", error);
    }
  }, [
    isWindowFocused,
    vadInstance,
    scriptsLoaded,
    entered,
    micActiveAI,
    voiceMode,
    chatRecording,
    startRecording,
    stopRecording,
    isTalking
  ]);

  return (
    <VADContext.Provider
      value={{
        isRecording,
        startRecording,
        stopRecording,
        scriptsLoaded,
        chatRecording,
        setChatRecording,
        voiceMode,
        setVoiceMode,
        isTalking,
        setIsTalking
      }}
    >
      {children}
    </VADContext.Provider>
  );
});

VADProvider.propTypes = {
  children: PropTypes.node.isRequired,
  entered: PropTypes.bool.isRequired,
  onSpeechEndCallback: PropTypes.func,
  onSpeechStartCallback: PropTypes.func,
  options: PropTypes.object,
  micActiveAI: PropTypes.bool.isRequired
};
